/**
 * Admin — Your authenticator (TOTP enrolment)
 *
 * This enrols an authenticator app against YOUR OWN account. It is not an admin
 * screen for managing other people's factors, and deliberately cannot be: the
 * enrol and verify calls only ever act on the caller's session, so there is no
 * route here to touch anyone else.
 *
 * ── WHY IT EXISTS ──────────────────────────────────────────────────────────────
 *
 * Masquerade (Support Access, epic 86d467vyj) asks a super admin for a six-digit
 * code from their own authenticator before it will enter a member's account. It
 * only ever VERIFIES a factor — it has no way to create one. On 27 Sep 2026 that
 * left the feature unusable in every environment: `auth.mfa_factors` was empty on
 * both dev and prod, across all super admins, and nothing anywhere in the app
 * could enrol one. This is that missing piece.
 *
 * ── WHERE THIS LIVES, AND WHY NOT IN MEMBER SETTINGS ───────────────────────────
 *
 * It sits under /admin rather than /settings because putting a Security tab in
 * member settings decides something nobody has decided yet — whether every member
 * is offered two-factor. That is a product call. Anyone who can reach /admin can
 * enrol their own authenticator here, which covers the people who need it today
 * without answering that question. Move it to member settings when the answer is
 * yes rather than duplicating it.
 *
 * ── THE SECRET ────────────────────────────────────────────────────────────────
 *
 * The QR code, the secret and the URI are shown once, on screen, and never logged
 * — the auth library's own types say so explicitly. They are held in component
 * state for the length of the enrolment and dropped when it finishes or is
 * cancelled. Nothing is written to storage and nothing crosses the network except
 * the enrol and verify calls themselves.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OneTimeCodeInput } from '@/components/ui/OneTimeCodeInput';
import { formatDateShort } from '@/lib/dates';

export const Route = createFileRoute('/admin/security')({
  component: SecurityPage,
});

interface EnrolmentInProgress {
  factorId: string;
  /** The `otpauth://` URI. The QR is drawn from THIS, not from the server's image. */
  uri: string;
  secret: string;
}

function SecurityPage() {
  const queryClient = useQueryClient();
  const [enrolment, setEnrolment] = useState<EnrolmentInProgress | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const factorsQuery = useQuery({
    queryKey: ['mfa-factors'],
    queryFn: async () => {
      const { data, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw listError;
      return data;
    },
  });

  /** Verified TOTP factors — what masquerade actually looks for. */
  const verified = factorsQuery.data?.totp ?? [];
  /**
   * Factors left behind by an enrolment nobody finished. Worth surfacing rather
   * than hiding: there is a cap of ten factors per account, and a stalled
   * unverified one still counts towards it while doing nothing.
   */
  const unverified = (factorsQuery.data?.all ?? []).filter(
    (f) => f.factor_type === 'totp' && f.status !== 'verified',
  );

  const start = useMutation({
    mutationFn: async () => {
      const { data, error: enrolError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        // Unique, because GoTrue refuses a duplicate friendly name on the same
        // account and a stalled enrolment would otherwise block every retry.
        friendlyName: `Authenticator ${new Date().toISOString().slice(0, 16)}`,
        issuer: 'Alfie',
      });
      if (enrolError) throw enrolError;
      return data;
    },
    onSuccess: (data) => {
      setError(null);
      setCode('');
      setEnrolment({
        factorId: data.id,
        uri: data.totp.uri,
        secret: data.totp.secret,
      });
    },
    onError: (err) => setError(describeEnrolError(err)),
  });

  const confirm = useMutation({
    mutationFn: async (submitted: string) => {
      if (!enrolment) throw new Error('No enrolment in progress');
      // challengeAndVerify, not challenge-then-verify: GoTrue refuses a verify
      // whose network address differs from the one that made the challenge, and
      // one call cannot disagree with itself.
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: enrolment.factorId,
        code: submitted,
      });
      if (verifyError) throw verifyError;
    },
    onSuccess: async () => {
      setEnrolment(null);
      setCode('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['mfa-factors'] });
    },
    onError: (err) => {
      setCode('');
      setError(err instanceof Error ? err.message : 'That code was not accepted.');
    },
  });

  const remove = useMutation({
    mutationFn: async (factorId: string) => {
      const { error: unenrolError } = await supabase.auth.mfa.unenroll({ factorId });
      if (unenrolError) throw unenrolError;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mfa-factors'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not remove it.'),
  });

  const busy = start.isPending || confirm.isPending || remove.isPending;

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-foreground">Your authenticator</h1>
        <p className="text-sm text-muted-foreground">
          A six-digit code from an authenticator app on your phone. Masquerading as a member asks
          for one before it will enter their account.
        </p>
      </header>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {factorsQuery.isLoading && (
        <p className="text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
          Checking what you have set up…
        </p>
      )}

      {factorsQuery.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Could not read your authenticator setup:{' '}
            {factorsQuery.error instanceof Error ? factorsQuery.error.message : 'unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* ── Already set up ──────────────────────────────────────────────────── */}
      {verified.length > 0 && (
        <section className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <p className="text-sm font-medium text-foreground">Authenticator active</p>
                <p className="text-[13px] text-muted-foreground">
                  You can masquerade as a member, once the feature is switched on.
                </p>
              </div>
              <ul className="space-y-2">
                {verified.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2"
                  >
                    <span className="min-w-0 text-[13px]">
                      <span className="block truncate text-foreground">
                        {f.friendly_name ?? 'Authenticator'}
                      </span>
                      <span className="text-muted-foreground">
                        Added {formatDateShort(f.created_at)}
                      </span>
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => remove.mutate(f.id)}
                      className="shrink-0 text-danger hover:text-danger"
                    >
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                Removing your only authenticator stops you masquerading until you add another.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ── Enrolment in progress ───────────────────────────────────────────── */}
      {enrolment && (
        <section className="space-y-4 rounded-xl border border-border bg-card p-5">
          <div>
            <p className="text-sm font-medium text-foreground">Scan this with your app</p>
            <p className="text-[13px] text-muted-foreground">
              Google Authenticator, 1Password, Authy — any of them. Then enter the six digits it
              shows.
            </p>
          </div>

          {/*
            Drawn locally from the otpauth:// URI rather than shown from the server's
            own image. GoTrue returns a `qr_code` field too, and its docs say to make a
            src by prepending `data:image/svg+xml;utf-8,` — but that MIME parameter is
            malformed (`charset=utf-8` is the real one) and the browser renders a broken
            image, which is exactly what happened on prod on 27 Sep 2026.
            `qrcode.react` was already a dependency, needs no encoding decisions, and
            draws from the one value that is unambiguously data. Do not go back to the
            server's image.

            White plate in both themes: a QR inverted in dark mode will not scan.
          */}
          <div className="flex justify-center rounded-lg bg-white p-4">
            <QRCodeSVG
              value={enrolment.uri}
              size={176}
              // Highest error correction: these get scanned off a screen at an angle,
              // often a laptop behind a phone.
              level="H"
              title="Authenticator setup QR code"
            />
          </div>

          <details className="text-[13px]">
            <summary className="cursor-pointer text-muted-foreground">
              Can't scan it? Enter this key by hand
            </summary>
            <code className="mt-2 block break-all rounded-md bg-secondary px-3 py-2 font-mono text-xs text-foreground">
              {enrolment.secret}
            </code>
          </details>

          <div className="space-y-3">
            <div className="flex justify-center">
              <OneTimeCodeInput
                value={code}
                onChange={(next) => {
                  setCode(next);
                  if (error) setError(null);
                }}
                onComplete={(next) => {
                  if (!busy) confirm.mutate(next);
                }}
                disabled={busy}
                autoFocus
                aria-label="Six digit code from your authenticator app"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  // Drop the half-finished factor rather than leaving it against
                  // the ten-factor cap.
                  remove.mutate(enrolment.factorId);
                  setEnrolment(null);
                  setCode('');
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button disabled={busy || code.length !== 6} onClick={() => confirm.mutate(code)}>
                {confirm.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                )}
                <Check className="mr-1.5 h-4 w-4" aria-hidden />
                Confirm
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ── Nothing set up yet ──────────────────────────────────────────────── */}
      {!enrolment && !factorsQuery.isLoading && verified.length === 0 && (
        <section className="rounded-xl border border-border bg-card p-5">
          <p className="text-sm font-medium text-foreground">No authenticator yet</p>
          <p className="mt-1 text-[13px] text-muted-foreground">
            You will need an authenticator app on your phone before you can masquerade as a
            member.
          </p>
          <Button className="mt-4" disabled={busy} onClick={() => start.mutate()}>
            {start.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            Set up an authenticator
          </Button>
        </section>
      )}

      {/* ── Leftovers from an abandoned attempt ─────────────────────────────── */}
      {unverified.length > 0 && !enrolment && (
        <section className="rounded-xl border border-warning/40 bg-warning/[0.06] p-5">
          <p className="text-sm font-medium text-foreground">
            {unverified.length} unfinished {unverified.length === 1 ? 'attempt' : 'attempts'}
          </p>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Started but never confirmed, so they do nothing. Each one still counts against the
            limit of ten, so they are worth clearing out.
          </p>
          <ul className="mt-3 space-y-2">
            {unverified.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-3 text-[13px]">
                <span className="min-w-0 truncate text-muted-foreground">
                  {f.friendly_name ?? f.id} · started {formatDateShort(f.created_at)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => remove.mutate(f.id)}
                  className="shrink-0"
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * Enrolment refusals, in plain words.
 *
 * The one worth naming is a project with TOTP switched off in its auth settings:
 * GoTrue answers with an unhelpful 422 and the reader has no way to know the fix
 * is a dashboard toggle rather than anything on this page. That was an open
 * question on both dev and prod on 27 Sep 2026, so this message is the answer to
 * it arriving at the moment it is needed.
 */
function describeEnrolError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  if (/disabled|not enabled|unsupported/i.test(message)) {
    return (
      'This project has authenticator sign-in switched off, so a factor cannot be created. ' +
      'Turn on MFA (TOTP) in the Supabase dashboard under Authentication, then try again. ' +
      `The server said: ${message}`
    );
  }
  if (/too many|limit/i.test(message)) {
    return `You already have the maximum number of factors. Remove one first. The server said: ${message}`;
  }
  return message;
}
